import * as yup from 'yup';
import { isValidBirthDate } from '~/pages/PersonalCoverage/newJourney/birthDateValidation';
import { isValidCpf } from '~/utils/isValidCpf';

const zipcodeHasEightDigits = (zip: string | undefined) => (zip?.replace(/\D/g, '').length ?? 0) === 8;

export default yup
  .object({
    number: yup
      .string()
      .required('Número do cartão é obrigatório')
      .test(`test-card-number-length`, 'Digite o número completo, este parece incompleto.', (value) => {
        if (value) {
          const newValue = value.replace(/\s/g, '').trim();
          if (newValue.length > 14) return true;
        }
        return false;
      }),
    cvv: yup.string().min(3, '3 a 5 dígitos').required('Digite um CVV válido (3 a 5 dígitos).'),
    expiry: yup
      .string()
      .required('Data de expiração do cartão é obrigatório')
      .test(`test-expiry-length`, 'Digite uma data válida (mês/ano).', (value) => {
        if (value) {
          const date = new Date().getFullYear().toString().substring(-2);
          const month = parseInt(value.substring(0, 2));
          const year = parseInt(value.substring(3, 5));
          if (month > 12) {
            return false;
          }
          const newYear = parseInt(date) - 2000;
          if (year < newYear) {
            return false;
          }
        }
        return true;
      }),
    name: yup
      .string()
      .required('Nome no cartão é obrigatório')
      .test(`test-name-complete-length`, 'Digite o nome completo, este parece incompleto.', (value) => {
        if (
          value &&
          value
            .trim()
            .split(' ')
            .filter((word) => word.length > 0).length > 1
        )
          return true;
        return false;
      }),
    zipcode: yup
      .string()
      .required('Este é um CEP inválido, digite um válido. Em caso de dúvidas, consulte o site dos Correios.')
      .test(
        `test-zipcode-length`,
        `Este é um CEP inválido, digite um válido. Em caso de dúvidas, 
      consulte o site dos Correios.`,
        (value) => {
          if (value) {
            // Remove todos os caracteres não numéricos
            const newValue = value.replace(/\D/g, '');
            if (newValue.length === 8) return true;
          }
          return false;
        }
      ),
    numberAddress: yup
      .string()
      .required('Digite o número para termos o seu endereço completo.')
      .max(8, 'Máximo de 8 dígitos.'),
    state: yup
      .string()
      .when('zipcode', (zipcode: string | undefined, schema) =>
        zipcodeHasEightDigits(zipcode)
          ? schema.required('Estado é obrigatório.').max(2, 'Máximo de 2 caracteres.')
          : schema.notRequired()
      ),
    city: yup
      .string()
      .when('zipcode', (zipcode: string | undefined, schema) =>
        zipcodeHasEightDigits(zipcode) ? schema.required('Cidade é obrigatória.') : schema.notRequired()
      ),
    neighborhood: yup
      .string()
      .when('zipcode', (zipcode: string | undefined, schema) =>
        zipcodeHasEightDigits(zipcode) ? schema.required('Bairro é obrigatório.') : schema.notRequired()
      ),
    street: yup
      .string()
      .when('zipcode', (zipcode: string | undefined, schema) =>
        zipcodeHasEightDigits(zipcode) ? schema.required('Endereço é obrigatório.') : schema.notRequired()
      ),
    additionalDetails: yup.string(),

    // «Dados do titular» (Figma 1680:5415) — vão em PUT /v2/Quotes/{hash}/customer.
    holderName: yup
      .string()
      .required('Nome do titular é obrigatório')
      .test('test-holder-name-complete', 'Digite o nome completo, este parece incompleto.', (value) =>
        Boolean(
          value &&
            value
              .trim()
              .split(' ')
              .filter((word) => word.length > 0).length > 1
        )
      ),
    holderDocument: yup
      .string()
      .required('CPF do titular é obrigatório')
      .test('test-holder-cpf', 'Digite um CPF válido.', (value) => (value ? isValidCpf(value) : false)),
    holderBirthDate: yup
      .string()
      .required('Data de nascimento é obrigatória')
      .test('test-holder-birthdate', 'Informe uma data válida (mínimo 18 anos).', (value) =>
        value ? isValidBirthDate(value) : false
      ),
    holderPhone: yup
      .string()
      .required('Telefone do titular é obrigatório')
      .test('test-holder-phone', 'Digite o telefone completo, este parece incompleto.', (value) =>
        value ? value.replace(/\D/g, '').length >= 10 : false
      ),
    holderEmail: yup.string().email('E-mail inválido').required('E-mail do titular é obrigatório')
  })
  .required();
